import { Button, Form, Input, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface LoginResetPasswordProps {
  onFinish: (data: { password: string }) => void;
}

/**
 * Form component for resetting/changing the user's password.
 * It validates the new password's strength and ensures the confirmation matches.
 */
export const LoginResetPassword = ({ onFinish }: LoginResetPasswordProps) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const handleFormFinish = (values: { password: string }) => {
    // Only the 'password' field is needed for the onFinish callback.
    // The 'confirmPassword' field is just for UI validation.
    onFinish({ password: values.password });
  };
  
  return (
    <div className="w-full max-w-sm mx-auto">
      <Title level={3} className="text-center font-azonix text-charcoal">
        Reset Password
      </Title>
      <Text type="secondary" className="block text-center mb-8">
        Enter your new password. It should be strong and secure.
      </Text>

      <Form
        form={form}
        name="reset-password"
        onFinish={handleFormFinish}
        layout="vertical"
        size="large"
        autoComplete="off"
      >
        <Form.Item
          label="New password"
          name="password"
          rules={[
            { required: true, message: 'Password is required.' },
            { min: 8, message: 'Password must be at least 8 characters long.' },
            { pattern: /[0-9]/, message: 'Must contain at least one number.' },
            { pattern: /[a-z]/, message: 'Must contain at least one lowercase letter.' },
            { pattern: /[A-Z]/, message: 'Must contain at least one uppercase letter.' },
            { pattern: /[\W_]/, message: 'Must contain at least one special character.' },
          ]}
          hasFeedback // Provides visual feedback (success/error icon)
        >
          <Input.Password placeholder="Enter the new password" />
        </Form.Item>

        <Form.Item
          label="Confirm new password"
          name="confirmPassword"
          dependencies={['password']} // Ensures this field is re-validated when 'password' changes
          rules={[
            { required: true, message: 'Password confirmation is required.' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('The passwords do not match.'));
              },
            }),
          ]}
          hasFeedback
        >
          <Input.Password placeholder="Confirm the new password" />
        </Form.Item>

        <Form.Item className="mt-6">
          <Button type="primary" htmlType="submit" block>
            Reset Password
          </Button>
        </Form.Item>

        <div className="text-center">
          <Button type="link" onClick={() => navigate('/login')}>
            Return to login
          </Button>
        </div>
      </Form>
    </div>
  );
};